import i18n from '../i18n/i18n';

export type CanonicalPlanId = 'free' | 'starter' | 'growth' | 'enterprise';

export interface WebPlanDefinition {
  id: CanonicalPlanId;
  label: string;
  tagline: string;
  priceMonthly: number | null;
  order: number;
}

const PLAN_DEFINITIONS: Record<CanonicalPlanId, WebPlanDefinition> = {
  free: {
    id: 'free',
    label: 'Free',
    tagline: 'For individuals and side projects',
    priceMonthly: 0,
    order: 0,
  },
  starter: {
    id: 'starter',
    label: 'Starter',
    tagline: 'For small teams and growing businesses',
    priceMonthly: 49,
    order: 1,
  },
  growth: {
    id: 'growth',
    label: 'Growth',
    tagline: 'For scaling teams that need full compliance coverage',
    priceMonthly: 149,
    order: 2,
  },
  enterprise: {
    id: 'enterprise',
    label: 'Enterprise',
    tagline: 'Custom limits, SLA, and dedicated support',
    priceMonthly: null,
    order: 3,
  },
};

export const WEB_PLAN_DEFINITIONS = [
  PLAN_DEFINITIONS.free,
  PLAN_DEFINITIONS.starter,
  PLAN_DEFINITIONS.growth,
  PLAN_DEFINITIONS.enterprise,
] as const;

export function normalizePlanId(plan: string | null | undefined): CanonicalPlanId | null {
  if (!plan) return null;
  switch (plan) {
    case 'free':
    case 'starter':
    case 'growth':
    case 'enterprise':
      return plan;
    case 'professional':
      return 'growth';
    default:
      return null;
  }
}

export function getPlanDefinition(plan: string | null | undefined): WebPlanDefinition | null {
  const normalized = normalizePlanId(plan);
  return normalized ? PLAN_DEFINITIONS[normalized] : null;
}

export function getPlanLabel(plan: string | null | undefined): string {
  const normalized = normalizePlanId(plan);
  if (normalized) {
    return i18n.t(`common:plans.${normalized}` as const);
  }
  if (!plan) return i18n.t('common:plans.unknown');
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

export function getPlanOrder(plan: string | null | undefined): number {
  return getPlanDefinition(plan)?.order ?? -1;
}

export function formatPlanLimit(limit: number): string {
  return limit.toLocaleString();
}

export function buildBillingUpgradePath(plan: string, source: string): string {
  const normalized = normalizePlanId(plan) ?? plan;
  const query = new URLSearchParams({
    plan: normalized,
    source,
  });
  return `/billing?${query.toString()}`;
}
